'use client'

import * as React from 'react'
import { Switch as HeadlessSwitch } from '@headlessui/react'
import { cn } from '@/lib/utils'

export interface SwitchProps {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  label?: string
  disabled?: boolean
  size?: 'sm' | 'default'
  className?: string
}

const sizeConfig = {
  sm: {
    track: 'w-8 h-5',
    knob: 'h-3.5 w-3.5',
    translate: 'translate-x-3.5',
  },
  default: {
    track: 'w-10 h-6',
    knob: 'h-4 w-4',
    translate: 'translate-x-5',
  },
} as const

const Switch: React.FC<SwitchProps> = ({
  checked = false,
  onCheckedChange,
  label,
  disabled = false,
  size = 'default',
  className,
}) => {
  const config = sizeConfig[size]

  const switchElement = (
    <HeadlessSwitch
      checked={checked}
      onChange={(val: boolean) => onCheckedChange?.(val)}
      disabled={disabled}
      className={cn(
        'relative inline-flex items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-lime)] focus-visible:ring-offset-2 ring-offset-background',
        config.track,
        checked ? 'bg-[var(--color-lime)]' : 'bg-muted',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block rounded-full bg-[var(--destructive-foreground)] shadow-sm transition-transform transform',
          config.knob,
          checked ? config.translate : 'translate-x-1',
        )}
      />
    </HeadlessSwitch>
  )

  if (!label) {
    return <div className={className}>{switchElement}</div>
  }

  return (
    <label className={cn('flex items-center gap-3 cursor-pointer', disabled && 'opacity-50 cursor-not-allowed', className)}>
      {switchElement}
      <span className="text-sm font-medium text-foreground">{label}</span>
    </label>
  )
}

export { Switch }
